import React, { useContext } from 'react';
import {
  CloudDownloadOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Button, message, Modal, Space, Tooltip, Upload } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { btnActive } from '../../../../styles';
import { GRAPHICS } from '../../../../constants/define/translation';
import { CONFIG_CONTAINER_NAME, deleteBlob, uploadFileToBlob } from '../../../../utils/azure';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { deleteImageRepository, updateImageRepository } from '../../../../repository/mytm/image/imageRepository';

interface ImageActionsColumnProps {
  record: any;
  onRefresh?: (loading?: boolean) => void;
  onLoading: (loading: boolean) => void;
  onHandleClickBtnEdit: (record: any) => void;
}
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
const azureCDN = process.env.REACT_APP_AZURE_CDN;
export default React.memo((props: ImageActionsColumnProps) => {
  const { t } = useTranslation(GRAPHICS);
  const { record, onRefresh, onLoading, onHandleClickBtnEdit } = props;
  const userContext = useContext(UserContext);
  const onHandleUpload = async (options: any) => {
    const { onError, file } = options;
    try {
      onLoading(true);
      const resultUpload = await uploadFileToBlob(
        [
          {
            blob: file,
            fileType: file.type,
            fileName: file.name,
          },
        ],
        CONFIG_CONTAINER_NAME.UPLOADS,
      );
      if (resultUpload && resultUpload.length > 0) {
        const url = resultUpload[0];
        const newUrlCDN = azureCDN ? url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN) : url;
        const payload = {
          id: record.id,
          keyImage: record.keyImage,
          urlImage: newUrlCDN,
        };
        await updateImageRepository(payload);
        message.success(t('message.uploadImageSuccess'));
        onRefresh && onRefresh(true);
        onLoading(false);
      }
    } catch (error) {
      onLoading(false);
      onError({ error });
    }
  };
  const onHandleDelete = async (options: any) => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.deleteImage')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        onLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const onFunc = () => {
          deleteImageRepository(record.id)
            .then(() => {
              deleteBlob(record?.urlImage, CONFIG_CONTAINER_NAME.UPLOADS).then().catch();
              onRefresh && onRefresh(true);
              message.success(t('message.deleteImageSuccess'));
              onLoading(false);
            })
            .catch(error => {
              message.error(error.message);
              onLoading(false);
            });
        };

        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
          onLoading(false);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              onFunc();
            })
            .catch((e: any) => {
              onLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const onHandleDownload = () => {
    fetch(record?.urlImage)
      .then(resp => resp.blob())
      .then(blob => {
        message.success(t('message.downloadImageSuccess'));
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = record?.urlImage.substring(record?.urlImage?.lastIndexOf('/') + 1, record?.urlImage?.length);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => MESSAGE_ERROR.M02);
  };
  const onHandleClickBtnEditImage = () => {
    onHandleClickBtnEdit(record);
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.action.edit')}>
          <Button icon={<EditOutlined />} onClick={onHandleClickBtnEditImage} />
        </Tooltip>
        <Tooltip title={t('table.action.download')}>
          <Button icon={<CloudDownloadOutlined />} onClick={onHandleDownload} />
        </Tooltip>
        <Tooltip title={t('table.action.upload')}>
          <Upload listType="picture" maxCount={1} showUploadList={false} customRequest={onHandleUpload}>
            <Button icon={<CloudUploadOutlined />} className="active" />
          </Upload>
        </Tooltip>
        <Tooltip title={t('table.action.delete')}>
          <Button icon={<DeleteOutlined />} onClick={onHandleDelete} />
        </Tooltip>
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.5rem;
  ${btnActive}
  button {
    cursor: pointer;
  }
`;
